import { useChat } from "../../../Context/ChatContext";
import { useAuth } from "../../../Context/AuthContext";
import { User, Database, Zap, MessageCircle, Copy, Check } from "react-feather";
import welcomeLogo from "../../../Assets/welcome-logo.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState } from "react";

const IntellachatMessages = () => {
  const { chatMessages } = useChat();
  const { currentProfile } = useAuth();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get icon based on status
  const getStatusIcon = (status: string) => {
    if (status.includes('database') || status.includes('Querying')) {
      return <Database size={14} className="text-accent-teal" />;
    }
    if (status.includes('Processing') || status.includes('results')) {
      return <Zap size={14} className="text-yellow-400" />;
    }
    if (status.includes('Generating') || status.includes('response')) {
      return <MessageCircle size={14} className="text-accent-purple" />;
    }
    return null;
  };

  // User avatar component
  const UserAvatar = () => {
    if (currentProfile?.profile_photo) {
      return (
        <img
          src={currentProfile.profile_photo}
          alt="User"
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-dark-card-hover border border-dark-border flex items-center justify-center">
        <User size={16} className="text-gray-400" />
      </div>
    );
  };

  // AI avatar component
  const AIAvatar = () => (
    <img src={welcomeLogo} alt="AI" className="w-12 h-12 flex-shrink-0" />
  );

  return (
    <div className="flex flex-col space-y-6 py-4">
      {chatMessages.map((message, index) => {
        if (message.sender === 'loading') {
          // Strip trailing dots from message for animation
          const textWithoutDots = (message.message || 'Thinking').replace(/\.+$/, '');

          return (
            <div key={index} className="flex items-start gap-4">
              {/* AI Avatar */}
              <AIAvatar />
              {/* Loading Status with shimmer effect */}
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 mt-3">
                  {getStatusIcon(message.message)}
                  <span className="text-sm loading-status">
                    {textWithoutDots}<span className="animated-dots"></span>
                  </span>
                </div>
              </div>
              <style>{`
                .loading-status {
                  background: linear-gradient(90deg, #9ca3af 0%, #ffffff 50%, #9ca3af 100%);
                  background-size: 200% 100%;
                  -webkit-background-clip: text;
                  background-clip: text;
                  -webkit-text-fill-color: transparent;
                  animation: shimmer 1.5s ease-in-out infinite;
                }
                @keyframes shimmer {
                  0% { background-position: 100% 0; }
                  100% { background-position: -100% 0; }
                }
                .animated-dots::after {
                  content: '';
                  animation: dots 2s steps(1, end) infinite;
                }
                @keyframes dots {
                  0% { content: ''; }
                  14% { content: '.'; }
                  28% { content: '..'; }
                  42% { content: '...'; }
                  58% { content: '...'; }
                  72% { content: '..'; }
                  86% { content: '.'; }
                  100% { content: ''; }
                }
              `}</style>
            </div>
          );
        }

        const isUser = message.sender === 'user';

        if (isUser) {
          return (
            <div key={index} className="flex items-start flex-row-reverse gap-1">
              <div className="-mt-3">
                <UserAvatar />
              </div>
              <div className="flex-1 max-w-[80%] flex justify-end">
                <div className="inline-block px-4 py-3 rounded-2xl rounded-tr-none bg-gradient-to-r from-accent-teal via-blue-500 to-accent-purple text-white">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.message}</p>
                </div>
              </div>
            </div>
          );
        }

        // AI message - Gemini style (no bubble) with markdown rendering
        return (
          <div key={index} className="flex items-start gap-4 group">
            <AIAvatar />
            <div className="flex-1 pt-2">
              <div className="prose prose-invert prose-sm max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p className="text-sm leading-relaxed text-gray-200 mb-3">{children}</p>,
                    strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-3">
                        <table className="min-w-full text-sm border border-dark-border rounded-lg overflow-hidden">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-dark-card-hover">{children}</thead>,
                    th: ({ children }) => <th className="px-3 py-2 text-left text-gray-300 font-medium border-b border-dark-border">{children}</th>,
                    td: ({ children }) => <td className="px-3 py-2 text-gray-200 border-b border-dark-border">{children}</td>,
                    tr: ({ children }) => <tr className="hover:bg-dark-card-hover">{children}</tr>,
                    ul: ({ children }) => <ul className="list-disc list-inside text-gray-200 space-y-1 my-2">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside text-gray-200 space-y-1 my-2">{children}</ol>,
                    li: ({ children }) => <li className="text-sm text-gray-200">{children}</li>,
                    code: ({ children }) => <code className="bg-dark-card px-1.5 py-0.5 rounded text-accent-teal text-xs">{children}</code>,
                    h1: ({ children }) => <h1 className="text-lg font-semibold text-white mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-semibold text-white mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold text-white mb-1">{children}</h3>,
                  }}
                >
                  {message.message}
                </ReactMarkdown>
              </div>
              {/* Copy button */}
              <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleCopy(message.message, index)}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-dark-card-hover"
                >
                  {copiedIndex === index ? (
                    <>
                      <Check size={14} className="text-green-400" />
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IntellachatMessages;
